/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import { css, jsx } from '@emotion/react'
import { Component, Fragment } from 'react'
import { isString } from 'lodash'

import { EuiFlexGroup, EuiFlexItem, EuiSpacer } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { StackDeployment } from '@modules/ui-types'

import SpinButton from '../SpinButton'

import Method from './Method'
import Path from './Path'
import Body from './Body'
import AdvancedFields from './AdvancedFields'
import AdvancedModeSwitch from './AdvancedModeSwitch'
import RecentHistoryButton from './RecentHistoryButton'
import CrossTabHistoryReplication from './CrossTabHistoryReplication'

import type { ConsoleRequestState } from '../../reducers/apiConsole'

export interface Props {
  inProgress: boolean
  request: ConsoleRequestState
  requestHistory: ConsoleRequestState[]
  clearDeploymentProxyResponse: (deploymentId: string) => void
  clearAPIConsoleHistory: () => void
  onChange: (request: ConsoleRequestState, deployment: StackDeployment) => void
  submitQueryToDeploymentProxy: (request: ConsoleRequestState) => void
  deployment: StackDeployment
}

class ApiConsoleRequest extends Component<Props> {
  render() {
    const { inProgress, request, requestHistory, clearAPIConsoleHistory, onChange, deployment } =
      this.props

    return (
      <section>
        <form
          onSubmit={this.onSubmit}
          css={css`
            .ReactCodeMirror {
              max-width: initial;
            }
          `}
          data-test-id='console-request-form'
        >
          <EuiFlexGroup alignItems='center' gutterSize='m'>
            <EuiFlexItem grow={false}>
              <Method
                value={request.method}
                disabled={inProgress}
                onChange={this.onFieldChange(`method`)}
              />
            </EuiFlexItem>

            <EuiFlexItem>
              <Path
                value={request.path}
                disabled={inProgress}
                onChange={this.onFieldChange(`path`)}
                append={
                  <RecentHistoryButton
                    history={requestHistory}
                    onClearHistory={clearAPIConsoleHistory}
                    onItemClick={this.onHistoryItemClick}
                  />
                }
              />
            </EuiFlexItem>

            <EuiFlexItem grow={false}>
              <SpinButton
                type='submit'
                fill={true}
                spin={inProgress}
                data-test-id='api-console-submit-request'
              >
                <FormattedMessage id='api-console-request.submit' defaultMessage='Submit' />
              </SpinButton>
            </EuiFlexItem>

            <EuiFlexItem grow={false}>
              <AdvancedModeSwitch
                checked={request.advancedMode}
                onChange={(isChecked) =>
                  onChange({ ...request, advancedMode: isChecked }, deployment)
                }
              />
            </EuiFlexItem>
          </EuiFlexGroup>

          {request.advancedMode && (
            <Fragment>
              <EuiSpacer size='m' />
              <AdvancedFields request={request} onChange={onChange} deployment={deployment} />
            </Fragment>
          )}

          <div
            css={css`
              margin-top: 0.625rem;
            `}
          >
            <Body
              value={request.body || ``}
              disabled={inProgress}
              onChange={this.onFieldChange(`body`)}
            />
          </div>
        </form>

        <CrossTabHistoryReplication />
      </section>
    )
  }

  onSubmit = (event) => {
    const { submitQueryToDeploymentProxy, request } = this.props
    event.preventDefault()
    submitQueryToDeploymentProxy(request)
  }

  onFieldChange = (field: string) => {
    const { request, onChange, deployment } = this.props

    return (event: React.FormEvent<HTMLInputElement>) => {
      const value = isString(event) ? event : (event.target as HTMLInputElement).value
      onChange({ ...request, [field]: value }, deployment)
    }
  }

  onHistoryItemClick = (historyItem: ConsoleRequestState) => {
    const { clearDeploymentProxyResponse, onChange, deployment } = this.props
    const { id } = deployment

    clearDeploymentProxyResponse(id)
    onChange(historyItem, deployment)
  }
}

export default ApiConsoleRequest
